import React, { lazy, Suspense } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "../components/ui/sonner";
import { useIsMobile } from "../hooks/useIsMobile";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ChatWidget from "../components/ChatWidget";
import AdminLayout from "../components/admin/AdminLayout";
import PlatformLayout from "../components/platform/PlatformLayout";
import { AuthProvider } from "../contexts/AuthContext";

import AdminUebersichtPage from "../pages/admin/AdminUebersichtPage";
import AdminCoLivingDashboardPage from "../pages/admin/AdminCoLivingDashboardPage";
import AdminApartmentsPage from "../pages/admin/AdminApartmentsPage";
import AdminBillingSettingsPage from "../pages/admin/AdminBillingSettingsPage";
import AdminLeadsPage from "../pages/admin/AdminLeadsPage";
import AdminTenantsPage from "../pages/admin/AdminTenantsPage";
import AdminTenanciesPage from "../pages/admin/AdminTenanciesPage";
import AdminTenancyRequirementsPage from "../pages/admin/AdminTenancyRequirementsPage";
import AdminAutomationsPage from "../pages/admin/AdminAutomationsPage";
import AdminTenantDetailPage from "../pages/admin/AdminTenantDetailPage";
import AdminLandlordsPage from "../pages/admin/AdminLandlordsPage";
import AdminLandlordDetailPage from "../pages/admin/AdminLandlordDetailPage";
import AdminUnitDetailPage from "../pages/admin/AdminUnitDetailPage";
import AdminInvoicesPage from "../pages/admin/AdminInvoicesPage";
import AdminInvoiceDetailPage from "../pages/admin/AdminInvoiceDetailPage";
import AdminInvoiceCreatePage from "../pages/admin/AdminInvoiceCreatePage";
import AdminInvoiceDashboardPage from "../pages/admin/AdminInvoiceDashboardPage";
import AdminBusinessApartmentsDashboardPage from "../pages/admin/AdminBusinessApartmentsDashboardPage";
import AdminPortfolioMapPage from "../pages/admin/AdminPortfolioMapPage";
import AdminInventoryPage from "../pages/admin/AdminInventoryPage";
import AdminInventoryDetailPage from "../pages/admin/AdminInventoryDetailPage";
import AdminDocumentsPage from "../pages/admin/AdminDocumentsPage";
import AdminTasksPage from "../pages/admin/AdminTasksPage";
import AdminTaskDetailPage from "../pages/admin/AdminTaskDetailPage";
import AdminWorkReportDetailPage from "../pages/admin/AdminWorkReportDetailPage";
import AdminObjektePage from "../pages/admin/AdminObjektePage";
import AdminRoomsPage from "../pages/admin/AdminRoomsPage";
import AdminRoomDetailPage from "../pages/admin/AdminRoomDetailPage";
import AdminOccupancyPage from "../pages/admin/AdminOccupancyPage";
import AdminRevenuePage from "../pages/admin/AdminRevenuePage";
import AdminRevenueAnalyticsPage from "../pages/admin/AdminRevenueAnalyticsPage";
import AdminExpensesPage from "../pages/admin/AdminExpensesPage";
import AdminFinanceDocumentsPage from "../pages/admin/AdminFinanceDocumentsPage";
import AdminDepositsPage from "../pages/admin/AdminDepositsPage";
import BexioLayout from "../components/admin/bexio/BexioLayout";
import AdminBexioPage from "../pages/admin/AdminBexioPage";
import BexioOverviewPage from "../pages/admin/bexio/BexioOverviewPage";
import BexioDraftsPage from "../pages/admin/bexio/BexioDraftsPage";
import BexioInvoicesPage from "../pages/admin/bexio/BexioInvoicesPage";
import BexioContactsPage from "../pages/admin/bexio/BexioContactsPage";
import BexioReconciliationPage from "../pages/admin/bexio/BexioReconciliationPage";
import BexioSyncRunsPage from "../pages/admin/bexio/BexioSyncRunsPage";
import BexioSettingsPage from "../pages/admin/bexio/BexioSettingsPage";
import AdminPerformancePage from "../pages/admin/AdminPerformancePage";
import AdminBreakEvenPage from "../pages/admin/AdminBreakEvenPage";
import AdminForecastPage from "../pages/admin/AdminForecastPage";
import AdminPropertyManagersPage from "../pages/admin/AdminPropertyManagersPage";
import AdminPropertyManagerDetailPage from "../pages/admin/AdminPropertyManagerDetailPage";
import AdminOwnersPage from "../pages/admin/AdminOwnersPage";
import AdminOwnerDetailPage from "../pages/admin/AdminOwnerDetailPage";
import AdminListingsPage from "../pages/admin/AdminListingsPage";
import RichTextFieldSmokeTest from "../pages/admin/__smoketest__/RichTextFieldSmokeTest";
import AdminApplicationsPage from "../pages/admin/AdminApplicationsPage";
import AdminApplicationDetailPage from "../pages/admin/AdminApplicationDetailPage";
import AdminPropertiesPage from "../pages/admin/AdminPropertiesPage";
import AdminPropertyDetailPage from "../pages/admin/AdminPropertyDetailPage";
import AdminUsersPage from "../pages/admin/AdminUsersPage";
import AdminEmailSettingsPage from "../pages/admin/AdminEmailSettingsPage";
import AdminResponsibilityAreasPage from "../pages/admin/AdminResponsibilityAreasPage";
import AdminUserDetailPage from "../pages/admin/AdminUserDetailPage";
import PlatformDashboardPage from "../pages/platform/PlatformDashboardPage";
import PlatformOrganizationsPage from "../pages/platform/PlatformOrganizationsPage";
import PlatformOrganizationDetailPage from "../pages/platform/PlatformOrganizationDetailPage";
import PlatformAuditLogsPage from "../pages/platform/PlatformAuditLogsPage";
import AdminLoginPage from "../pages/admin/AdminLoginPage";
import TenantLayout from "../components/tenant/TenantLayout";
import TenantLoginPage from "../pages/tenant/TenantLoginPage";
import TenantOverviewPage from "../pages/tenant/TenantOverviewPage";
import TenantTenanciesPage from "../pages/tenant/TenantTenanciesPage";
import TenantInvoicesPage from "../pages/tenant/TenantInvoicesPage";
import ResetPasswordPage from "../pages/ResetPasswordPage";
import ForgotPasswordPage from "../pages/ForgotPasswordPage";
import VerifyEmailPage from "../pages/VerifyEmailPage";
import LandlordLayout from "../components/landlord/LandlordLayout";
import LandlordLoginPage from "../pages/landlord/LandlordLoginPage";
import LandlordOverviewPage from "../pages/landlord/LandlordOverviewPage";
import LandlordPropertiesPage from "../pages/landlord/LandlordPropertiesPage";
import LandlordUnitsPage from "../pages/landlord/LandlordUnitsPage";
import LandlordTenanciesPage from "../pages/landlord/LandlordTenanciesPage";
import LandlordInvoicesPage from "../pages/landlord/LandlordInvoicesPage";

const HomePage = lazy(() => import("../pages/HomePage"));
const ApartmentsPage = lazy(() => import("../pages/ApartmentsPage"));
const ApartmentDetailPage = lazy(() => import("../pages/ApartmentDetailPage"));
const ForCompaniesPage = lazy(() => import("../pages/ForCompaniesPage"));
const ForPropertyManagersPage = lazy(() => import("../pages/ForPropertyManagersPage"));
const AboutPage = lazy(() => import("../pages/AboutPage"));
const ContactPage = lazy(() => import("../pages/ContactPage"));
// Calendar carries the FullCalendar bundle (~150-200 kB gzip) — lazy-load
// it so the rest of the admin app doesn't pay for that weight up front.
const AdminCalendarPage = lazy(() => import("../pages/admin/AdminCalendarPage"));
const AdminTodoListsPage = lazy(() => import("../pages/admin/AdminTodoListsPage"));

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function AppRouter() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isPlatformRoute = location.pathname.startsWith("/platform");
  const isTenantRoute = location.pathname.startsWith("/tenant");
  const isLandlordRoute = location.pathname.startsWith("/landlord");
  const showPublicUI =
    !isAdminRoute && !isPlatformRoute && !isTenantRoute && !isLandlordRoute;

  // M2: shift sonner toasts to top-right on mobile/tablet-portrait so
  // they no longer visually overlap the new admin/platform top bar's
  // centered page-title. md:+ keeps top-center exactly as today.
  const isMobile = useIsMobile();
  const toasterPosition = isMobile ? "top-right" : "top-center";

  return (
    <div className="App">
      <ScrollToTop />
      {showPublicUI && <Header />}
      <main>
        <AuthProvider>
          <Suspense fallback={<div className="text-center py-20">Loading...</div>}>
            <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/apartments" element={<ApartmentsPage />} />
            <Route path="/wohnungen/:city" element={<ApartmentsPage />} />
            <Route path="/apartments/:id" element={<ApartmentDetailPage />} />
            <Route path="/for-companies" element={<ForCompaniesPage />} />
            <Route path="/for-property-managers" element={<ForPropertyManagersPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />

            {process.env.NODE_ENV !== 'production' && (
              <Route path="/admin/__smoketest__/rich-text" element={<RichTextFieldSmokeTest />} />
            )}

            <Route path="/admin" element={<AdminLayout />}>
              <Route path="login" element={<AdminLoginPage />} />
              <Route index element={<AdminUebersichtPage />} />
              <Route path="dashboard" element={<AdminUebersichtPage />} />
              <Route path="operations" element={<AdminCoLivingDashboardPage />} />
              <Route path="business-apartments-dashboard" element={<AdminBusinessApartmentsDashboardPage />} />
              <Route path="portfolio-map" element={<AdminPortfolioMapPage />} />
              <Route path="objekte-dashboard" element={<AdminObjektePage />} />
              <Route path="rechnungen-dashboard" element={<AdminInvoiceDashboardPage />} />
              <Route path="apartments" element={<AdminApartmentsPage />} />
              <Route path="listings" element={<AdminListingsPage />} />
              <Route path="applications/:id" element={<AdminApplicationDetailPage />} />
              <Route path="applications" element={<AdminApplicationsPage />} />
              <Route path="leads" element={<AdminLeadsPage />} />
              <Route path="tenants/:tenantId" element={<AdminTenantDetailPage />} />
              <Route path="tenants" element={<AdminTenantsPage />} />
              <Route path="tenancies" element={<AdminTenanciesPage />} />
              {/* Nachweise & Pflichten: the three obligations every tenancy
                  carries, listed across the whole portfolio. A sibling of
                  `tenancies` rather than a tab inside it — it is a working
                  queue over every contract, not a view of one. */}
              <Route path="nachweise" element={<AdminTenancyRequirementsPage />} />
              <Route path="landlords/:id" element={<AdminLandlordDetailPage />} />
              <Route path="landlords" element={<AdminLandlordsPage />} />
            <Route path="users/:userId" element={<AdminUserDetailPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            {/* T1b: E-Mail-Absender. Admin-only — the page checks the role
                before it reads, because every endpoint behind it is
                `require_roles("admin")`. */}
            <Route path="email-settings" element={<AdminEmailSettingsPage />} />
            {/* RESP1: per-organisation responsibility configuration. */}
            <Route
              path="responsibilities"
              element={<AdminResponsibilityAreasPage />}
            />
            {/* A4: Automation Center. Admin-only — the page checks the role
                before it reads, because every endpoint behind it is
                `require_roles("admin")`. */}
            <Route path="automations" element={<AdminAutomationsPage />} />
              <Route
                path="organizations"
                element={<Navigate to="/platform/organizations" replace />}
              />
              <Route path="properties/:propertyId" element={<AdminPropertyDetailPage />} />
              <Route path="properties" element={<AdminPropertiesPage />} />
              <Route path="bewirtschafter/:id" element={<AdminPropertyManagerDetailPage />} />
              <Route path="bewirtschafter" element={<AdminPropertyManagersPage />} />
              <Route path="owners/:id" element={<AdminOwnerDetailPage />} />
              <Route path="owners" element={<AdminOwnersPage />} />
              <Route path="invoices" element={<AdminInvoicesPage />} />
              {/* /new MUST come BEFORE /:id (R5: react-router v7
                  prefers literals over params at same depth, but
                  explicit ordering is the safer guarantee) */}
              <Route path="invoices/new" element={<AdminInvoiceCreatePage />} />
              <Route path="invoices/:id" element={<AdminInvoiceDetailPage />} />
              <Route path="revenue" element={<AdminRevenuePage />} />
              {/* B3's unified revenue contract. A sibling of `revenue`, not a
                  replacement: the legacy page stays routed until retiring it is
                  decided on its own terms. Not under `/admin/bexio` — the
                  expected side reads no Bexio table at all, and that prefix
                  would declare it provider data. */}
              <Route path="revenue-analytics" element={<AdminRevenueAnalyticsPage />} />
              <Route path="ausgaben" element={<AdminExpensesPage />} />
              <Route path="belege" element={<AdminFinanceDocumentsPage />} />
              <Route path="kautionen" element={<AdminDepositsPage />} />
              {/* Bexio is a workspace, not two loose pages: the shared shell
                  (title + tab bar) is the route element, so every page below
                  gets it without importing it. Both URLs are unchanged —
                  `index` IS `/admin/bexio`. */}
              <Route path="bexio" element={<BexioLayout />}>
                <Route index element={<BexioOverviewPage />} />
                <Route path="drafts" element={<BexioDraftsPage />} />
                <Route path="invoices" element={<BexioInvoicesPage />} />
                <Route path="contacts" element={<BexioContactsPage />} />
                <Route path="reconciliation" element={<BexioReconciliationPage />} />
                <Route path="sync-runs" element={<BexioSyncRunsPage />} />
                <Route path="settings" element={<BexioSettingsPage />} />
                {/* Transitional. The overview replaced the old long page, but the
                    contact- and invoice-mapping modals only exist on it, and
                    unrouting it would remove the product's only access to
                    mapping until those move to the Rechnungen/Kontakte pages.
                    Deliberately NOT in `bexioNav`: it is a bridge, not a
                    destination, so it gets no tab and no sidebar entry. */}
                <Route path="legacy" element={<AdminBexioPage />} />
              </Route>
              <Route path="performance" element={<AdminPerformancePage />} />
              <Route path="break-even" element={<AdminBreakEvenPage />} />
              <Route path="prognose" element={<AdminForecastPage />} />
              <Route path="units/:unitId" element={<AdminUnitDetailPage />} />
              <Route path="invoices/open" element={<AdminInvoicesPage />} />
              <Route path="invoices/paid" element={<AdminInvoicesPage />} />
              <Route path="invoices/overdue" element={<AdminInvoicesPage />} />
              <Route path="billing-settings" element={<AdminBillingSettingsPage />} />
              <Route path="units" element={<Navigate to="/admin/apartments" replace />} />
              {/* D4c2: the parameterised route stays ABOVE the list, the
                  same order the properties and inventory pairs use. */}
              <Route path="rooms/:roomId" element={<AdminRoomDetailPage />} />
              <Route path="rooms" element={<AdminRoomsPage />} />
              <Route path="occupancy" element={<AdminOccupancyPage />} />
              <Route path="inventory/:itemId" element={<AdminInventoryDetailPage />} />
              <Route path="inventory" element={<AdminInventoryPage />} />
              <Route path="documents" element={<AdminDocumentsPage />} />
              <Route path="todo-lists" element={<AdminTodoListsPage />} />
              <Route path="tasks" element={<AdminTasksPage />} />
              <Route path="tasks/:taskId" element={<AdminTaskDetailPage />} />
              {/* D2f: reports have a detail page but no list page of
                  their own — the overview is /admin/calendar?mode=reports. */}
              <Route
                path="work-reports/:workReportId"
                element={<AdminWorkReportDetailPage />}
              />
              <Route path="calendar" element={<AdminCalendarPage />} />
              <Route path="tenants/active" element={<AdminTenantsPage />} />
              <Route path="tenants/move-outs" element={<AdminTenantsPage />} />
              <Route path="contracts" element={<AdminLandlordsPage />} />
              <Route path="leads/inquiries" element={<AdminLeadsPage />} />
              <Route path="leads/followups" element={<AdminLeadsPage />} />
            </Route>

            <Route path="/platform" element={<PlatformLayout />}>
              <Route index element={<PlatformDashboardPage />} />
              <Route path="audit-logs" element={<PlatformAuditLogsPage />} />
              <Route
                path="organizations/:organizationId"
                element={<PlatformOrganizationDetailPage />}
              />
              <Route path="organizations" element={<PlatformOrganizationsPage />} />
            </Route>

            <Route path="/tenant" element={<TenantLayout />}>
              <Route path="login" element={<TenantLoginPage />} />
              <Route index element={<TenantOverviewPage />} />
              <Route path="tenancies" element={<TenantTenanciesPage />} />
              <Route path="invoices" element={<TenantInvoicesPage />} />
            </Route>

            <Route path="/landlord" element={<LandlordLayout />}>
              <Route path="login" element={<LandlordLoginPage />} />
              <Route index element={<LandlordOverviewPage />} />
              <Route path="properties" element={<LandlordPropertiesPage />} />
              <Route path="units" element={<LandlordUnitsPage />} />
              <Route path="tenancies" element={<LandlordTenanciesPage />} />
              <Route path="invoices" element={<LandlordInvoicesPage />} />
            </Route>
            </Routes>
          </Suspense>
        </AuthProvider>
      </main>
      {showPublicUI && <Footer />}
      <Toaster position={toasterPosition} />
      {showPublicUI && <ChatWidget />}
    </div>
  );
}
